/**
 * beatNebulaPipeline — a volumetric raymarcher inspired by Noztol's
 * "Beat Nebula" on Shadertoy.
 *
 * The original samples 9 frequency bands from a 1-row audio texture
 * (iChannel0). Here we expose 3 vec3 uniforms (bass, mid, treble)
 * that the JS side fills every frame — either with real audio
 * spectrum data (microphone input) or with synthetic time-varying
 * functions as a fallback.
 *
 * Shader does:
 *   1. Sample 9 color values from bass/mid/treble vectors
 *   2. Step 101 times through 3D space (variable max steps)
 *   3. Accumulate sin-based colored patterns along the ray
 *   4. Nested sin(dot) layers for fractal detail
 *   5. Symmetric space folding (rotational)
 *   6. Tone map with tanh, gamma correct
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 * Attribution: shader logic by Noztol (Shadertoy); NSPP integration
 * and synthetic band fallback mine.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface BeatNebulaPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uTreble: WebGLUniformLocation | null;
  /** Raw 9-band audio data computed each frame, written into the
   *  shader uniforms. Indexed 0..8 across bass(0-2)/mid(3-5)/treble(6-8). */
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Single fullscreen quad fragment shader. Port of Beat Nebula.
// The bass/mid/treble are passed in as uniforms from JS (either
// real audio spectrum or synthetic fallback).
const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uTreble;

void main() {
  vec3 bass = uBass;
  vec3 mid = uMid;
  vec3 treble = uTreble;

  // Gain to scale up brightness (same as original).
  bass = pow(bass, vec3(3.0)) * 2.5;
  mid = pow(mid, vec3(3.0)) * 2.5;
  treble = pow(treble, vec3(3.0)) * 2.5;

  // Time and resolution.
  float t = -uTime;
  vec3 r = vec3(uResolution, 0.0);

  // Normalized UV centered on screen.
  vec2 uv = (gl_FragCoord.xy - r.xy * 0.5) / r.y;

  vec4 o = vec4(0.0);

  // Variable step count for animated complexity.
  float maxSteps = 60.0 + 40.0 * sin(uTime * 0.5);

  float d = 0.0;
  float s = 0.0;

  // Raymarch through the volume.
  for (float i = 0.0; i < 101.0; i += 1.0) {
    if (i > maxSteps) break;

    s = 0.006 + abs(s) * 0.3;
    d += s;

    vec3 p = vec3(uv * d, d + uTime + (bass.x * 0.2));

    // 9 colors spatially separated.
    vec3 colorB = sin(d * 1.0 + vec3(0.3, 0.9, 0.5 + p.z)) * bass;
    vec3 colorM = sin(d * 1.5 + vec3(0.9, 0.5, 0.3 + p.x)) * mid;
    vec3 colorT = sin(d * 2.0 + vec3(0.5, 0.3, 0.9 + p.y)) * treble;

    o.rgb += (colorB + colorM + colorT) / (abs(s) + 1e-4);

    // Symmetric space folding (rotation matrix around z).
    float ca = cos(0.02 * t + p.z * 0.2);
    float sa = sin(0.02 * t + p.z * 0.2);
    p.xy = mat2(ca, sa, -sa, ca) * p.xy;

    s = sin(2.0 + p.y + p.x);

    // Nested sin(dot) layers — pseudo-fractal detail.
    for (float n = 2.0; n <= 20.0; n *= 2.0) {
      vec3 freq = p * n;
      vec3 timeMod = vec3(
        0.8,
        2.5 + sin(uTime * 0.25) * 0.2,
        1.5 + sin(uTime * 0.1) * 0.5
      );
      s -= abs(dot(0.2 * sin(freq), timeMod)) / n;
    }
  }

  // Tone mapping and gamma.
  o = pow(tanh(o * o / 2.5e7), vec4(0.4545));
  o.a = 1.0;

  outColor = o;
}
`;

/**
 * Compute synthetic audio band values for the given time. Returns
 * 9 values in a flat Float32Array, indexed bass(0-2)/mid(3-5)/treble(6-8).
 * These mimic the audio band shape that a real AnalyserNode would
 * produce, so the shader output is similar to audio-driven mode
 * even without a microphone.
 */
export function computeSyntheticBands(t: number, out: Float32Array): void {
  // Bass — slow LFO + medium oscillation
  out[0] = 0.5 + 0.5 * Math.sin(t * 1.7) + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.6 + 0.4 * Math.sin(t * 2.3) * Math.max(0, Math.sin(t * 0.3));
  out[2] = 0.4 + 0.5 * Math.sin(t * 3.1 + 0.7);
  // Mid
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.1 + 1.0) * (0.6 + 0.4 * Math.sin(t * 0.2));
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.7 + 2.3);
  out[5] = 0.4 + 0.5 * Math.sin(t * 2.5 + 3.1) * (0.7 + 0.3 * Math.sin(t * 0.3));
  // Treble
  out[6] = 0.3 + 0.6 * Math.sin(t * 4.0 + 0.5) * Math.pow(0.5 + 0.5 * Math.sin(t * 0.1), 2);
  out[7] = 0.4 + 0.5 * Math.sin(t * 5.7 + 1.7);
  out[8] = 0.3 + 0.6 * Math.sin(t * 8.0 + 2.1) * Math.max(0, Math.sin(t * 0.3));
}

/**
 * Compute audio band values from a real AudioContext analyser. Pass in
 * the analyser's getByteFrequencyData output (length 1024 typical)
 * and a 9-band output buffer. Maps the 9 Shadertoy sample positions
 * (0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950)
 * to actual frequency bins.
 */
export function computeRealtimeBands(
  frequencyData: Uint8Array,
  out: Float32Array,
): void {
  // The 9 sample positions from the original Shadertoy shader.
  // These are 1D positions in the audio texture (which is 1 row
  // wide). Map to frequency bin indices.
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  const N = frequencyData.length;
  for (let i = 0; i < 9; i++) {
    const x = positions[i];
    // Map [0, 1] to a bin index.
    const bin = Math.min(N - 1, Math.max(0, Math.floor(x * N)));
    // Average a small window around the bin for smoother response.
    let sum = 0;
    let count = 0;
    for (let k = -1; k <= 1; k++) {
      const b = bin + k;
      if (b >= 0 && b < N) {
        sum += frequencyData[b];
        count++;
      }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createBeatNebulaPipeline(gl: WebGL2RenderingContext): BeatNebulaPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uTreble: gl.getUniformLocation(program, "uTreble"),
    bands: new Float32Array(9),
  };
}

export function drawBeatNebula(
  gl: WebGL2RenderingContext,
  pipe: BeatNebulaPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  // Pipe.bands is filled by main.ts before this is called each frame.
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  if (pipe.uTreble) gl.uniform3f(pipe.uTreble, pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
